import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, CardHeader, Container, Grid, MenuItem, TextField, Typography } from "@mui/material";
import MaterialTable from "material-table";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";

import { Icons } from "../utils/Icon";
import { CustomStyles } from "../utils/Styled";
import { useAuth } from "../hooks/useAuth";
import { GetTokenIdentity } from "../utils/Token";

const FIXED_YEARS = 5;
const customerTypeOptions = ["All", "End User", "Dealer"];

const formatCurrency = (value) =>
    Number(value || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const formatDate = (value) => {
    if (!value) return "-";

    const parsed = moment(value);
    if (!parsed.isValid()) return value;

    return parsed.format("DD MMM YYYY");
};

const mapCustomerTypeToSegment = (value) => {
    if (value === "End User") return "end_users";
    if (value === "Dealer") return "dealers";
    return "all";
};

export const ContinuousActiveCustomersListPage = () => {
    const classes = CustomStyles;
    const navigate = useNavigate();
    const { state } = useLocation();
    const { token, logout } = useAuth();
    const identity = GetTokenIdentity(token);

    const year = Number(state?.year) || new Date().getFullYear();
    const years = Number(state?.years) || FIXED_YEARS;
    const countryId = Number(state?.countryId) || Number(identity?.user?.country_id);
    const periodFromYear = Number(state?.periodFromYear) || year - (years - 1);
    const periodToYear = Number(state?.periodToYear) || year;

    const [searchKeyword, setSearchKeyword] = useState("");
    const [customerType, setCustomerType] = useState("All");
    const searchInputRef = useRef(null);

    const [appliedSearchKeyword, setAppliedSearchKeyword] = useState("");
    const [appliedCustomerType, setAppliedCustomerType] = useState("All");
    const [data, setData] = useState([]);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [count, setCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [summary, setSummary] = useState({
        totalCount: Number(state?.totalActive) || 0,
        endUsersCount: Number(state?.endUsers) || 0,
        dealersCount: Number(state?.dealers) || 0,
    });

    const fetchContinuousActiveCustomers = useCallback(async () => {
        if (!countryId) {
            setData([]);
            setCount(0);
            return;
        }

        setIsLoading(true);
        try {
            const params = {
                year,
                years,
                segment: mapCustomerTypeToSegment(appliedCustomerType),
                page: page + 1,
                limit: perPage,
            };

            if (appliedSearchKeyword.trim()) {
                params.search = appliedSearchKeyword.trim();
            }

            const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/customer-dashboard/${countryId}/continuous-active-customers`, {
                params,
            });

            const payload = response?.data?.data || {};
            const rows = (payload.data || []).map((item) => ({
                code: item.customerCode,
                company_name: item.customerName,
                date_created: formatDate(item.dateCreated),
                customer_type: item.customerType,
                last_invoice_date: formatDate(item.lastInvoiceDate),
                years_total_sales: Number(item.yearsTotalSales || 0),
                lifetime_sales: Number(item.lifetimeSales || 0),
            }));

            setData(rows);
            setCount(Number(payload.total || 0));
            setSummary((prevSummary) => ({
                ...prevSummary,
                totalCount: Number(payload.total || 0),
            }));
        } catch (error) {
            if (error?.response?.status === 401) {
                logout("Your account has been logged out of a session.");
                return;
            }

            const message = error?.response?.data?.message || "Unable to fetch continuous active customers list.";
            Swal.fire({
                icon: "error",
                title: "Failed to Load Data",
                text: message,
                confirmButtonColor: "#fe7320",
            });
        } finally {
            setIsLoading(false);
        }
    }, [countryId, year, years, appliedCustomerType, page, perPage, appliedSearchKeyword, logout]);

    useEffect(() => {
        fetchContinuousActiveCustomers();
    }, [fetchContinuousActiveCustomers]);

    const columns = [
        {
            title: "Code",
            field: "code",
            cellStyle: { width: 130, maxWidth: 130 },
            headerStyle: { width: 130, maxWidth: 130 },
        },
        {
            title: "Company Name",
            field: "company_name",
        },
        {
            title: "Date Created",
            field: "date_created",
            cellStyle: { width: 150, maxWidth: 150 },
            headerStyle: { width: 150, maxWidth: 150 },
        },
        {
            title: "Customer Type",
            field: "customer_type",
            cellStyle: { width: 150, maxWidth: 150 },
            headerStyle: { width: 150, maxWidth: 150 },
        },
        {
            title: "Last Invoice Date",
            field: "last_invoice_date",
            cellStyle: { width: 170, maxWidth: 170 },
            headerStyle: { width: 170, maxWidth: 170 },
        },
        {
            title: "5-Year Total Sales",
            field: "years_total_sales",
            render: (rowData) => formatCurrency(rowData.years_total_sales),
            cellStyle: { width: 140, maxWidth: 140 },
            headerStyle: { width: 140, maxWidth: 140 },
        },
        {
            title: "Lifetime Sales",
            field: "lifetime_sales",
            render: (rowData) => formatCurrency(rowData.lifetime_sales),
            cellStyle: { width: 140, maxWidth: 140 },
            headerStyle: { width: 140, maxWidth: 140 },
        },
    ];

    return (
        <div>
            <div className={classes.background} />
            <Container maxWidth="xl" sx={{ py: 2 }} style={{ padding: 14 }}>
                <MaterialTable
                    title="5-Year Continuous Active Customers"
                    icons={Icons}
                    columns={columns}
                    data={data}
                    page={page}
                    totalCount={count}
                    isLoading={isLoading}
                    style={{ padding: "24px" }}
                    options={{
                        pageSize: perPage,
                        pageSizeOptions: [10, 20, 30, 50],
                        search: false,
                        filtering: false,
                        sorting: false,
                        actionsColumnIndex: -1,
                        headerStyle: {
                            fontWeight: "bold",
                            backgroundColor: "#f1f1f1",
                            color: "#212122",
                        },
                        maxBodyHeight: "600px",
                        paginationType: "stepped",
                        padding: "dense",
                        draggable: false,
                        rowStyle: () => ({ backgroundColor: "#ffffff" }),
                    }}
                    localization={{
                        pagination: {
                            labelRowsPerPage: "",
                            labelDisplayedRows: "{from}-{to} of {count}",
                            labelRowsSelect: "rows",
                        },
                        toolbar: {
                            nRowsSelected: "{0} row(s) selected",
                        },
                        header: {
                            actions: "Actions",
                        },
                        body: {
                            emptyDataSourceMessage: "No data to display",
                        },
                    }}
                    onChangePage={(nextPage) => setPage(nextPage)}
                    onChangeRowsPerPage={(nextPerPage) => {
                        setPerPage(nextPerPage);
                        setPage(0);
                    }}
                    components={{
                        Toolbar: () => (
                            <>
                                <Grid container spacing={2} sx={{ px: 0.1, pb: 1.5 }}>
                                    <Grid item xs={12} sm={8}>
                                        <CardHeader
                                            sx={{ p: 0 }}
                                            title={
                                                <Typography component="h1" variant="h5" color="primary" fontWeight="bold">
                                                    5-Year Continuous Active Customers: Existing Year
                                                </Typography>
                                            }
                                            subheader={
                                                <>
                                                    <Typography sx={{ mt: 0.8, color: "#2e3d4f", fontSize: "1rem" }}>
                                                        Criteria: Active sales generated consecutively from {periodFromYear} through {periodToYear}.
                                                    </Typography>
                                                    <Typography sx={{ mt: 0.4, color: "#2e3d4f", fontSize: "1rem" }}>
                                                        Total Active: {summary.totalCount} Accounts ({summary.endUsersCount} End Users | {summary.dealersCount} Dealers)
                                                    </Typography>
                                                </>
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4} sx={{ textAlign: { xs: "left", sm: "right" } }}>
                                        <Button color="error" onClick={() => navigate(-1)}>
                                            Back
                                        </Button>
                                    </Grid>
                                </Grid>

                                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, mt: 1.2, pb: 2 }}>
                                    <Box sx={{ flex: "1 1 320px", minWidth: 260, maxWidth: 420 }}>
                                        <TextField
                                            autoComplete="off"
                                            fullWidth
                                            size="small"
                                            label="Search by Name/Code"
                                            autoFocus={searchInputRef.current === document.activeElement}
                                            inputRef={searchInputRef}
                                            value={searchKeyword}
                                            onChange={(event) => setSearchKeyword(event.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: "0 0 220px", minWidth: 220 }}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="Customer Type"
                                            value={customerType}
                                            onChange={(event) => setCustomerType(event.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        >
                                            {customerTypeOptions.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Box>
                                    <Box sx={{ flex: "0 0 130px", minWidth: 130 }}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            sx={{ height: "40px", whiteSpace: "nowrap" }}
                                            onClick={() => {
                                                setAppliedSearchKeyword(searchKeyword);
                                                setAppliedCustomerType(customerType);
                                                setPage(0);
                                            }}
                                            disabled={isLoading || !countryId}
                                        >
                                            Show
                                        </Button>
                                    </Box>
                                </Box>
                            </>
                        ),
                    }}
                />
            </Container>
        </div>
    );
};
